import './OnboardingSurveyV2.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { completeSurvey, shuffleAnswers } from './helpers';
import { useNavigate } from 'react-router-dom';
import { analyticsTrack } from '../helpers/AnalyticsUtility';
import { saveEventToGoFormz } from '../helpers/GoFormzAnalytics';
import { SurveyAnswer, survey } from './dataV2';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Button, Card, Progress } from 'semantic-ui-react';
import { useAppSelector } from '../hooks/ReduxHooks';
import { useClientPersistedState } from '../persistedstate/clients/useClientPersistedState';

const PROGRESS_INCREMENT = 1;
const PROGRESS_STEPS = [34, 67, 100];
const STEP_COUNT = 3;

export default function OnboardingSurveyV2() {
  const navigate = useNavigate();
  const questionRef = useRef(null);
  const email = useAppSelector<string>((state) => state.profile.user.email);
  const [questionNumber, setQuestionNumber] = useState(1);
  const currentQuestion = survey[questionNumber];
  const [currentAnswer, setCurrentAnswer] = useState<string>();
  const [selectedAnswers, setSelectedAnswers] = useState<SurveyAnswer[]>([]);
  const [answers, setAnswers] = useState<SurveyAnswer[]>([]);
  const [fade, setFade] = useState(true);
  const [progress, setProgress] = useState(0);
  const [signupDomain, setSignupDomain] = useClientPersistedState('signupDomain');
  const industry = useAppSelector<string>((state) => state.profile?.client?.industry);
  const constructionOrManafacturingKeyWords = ['construction', 'manufacturing or machinery'];
  const constructionOrManufacturing = useMemo(
    () => industry && typeof industry === 'string' && constructionOrManafacturingKeyWords.some((x) => x === industry.toLowerCase()),
    [industry]
  );

  useEffect(() => {
    if (!signupDomain) {
      const domain = email.split('@')[1];
      setSignupDomain(domain);
    }
  }, [signupDomain]);

  const updateProgress = useCallback(() => {
    if (progress < PROGRESS_STEPS[questionNumber - 1]) {
      setProgress(progress + PROGRESS_INCREMENT);
    }
  }, [progress, questionNumber]);

  useEffect(() => {
    if (!currentQuestion) {
      return;
    }

    const delay = Math.ceil((currentQuestion.delay || 500) / 25) * 25;
    const interval = setInterval(updateProgress, delay / 25);
    return () => clearInterval(interval);
  }, [updateProgress, currentQuestion]);

  useEffect(() => {
    if (currentQuestion.last && progress === 100) {
      setTimeout(() => {
        navigate('/home', { replace: true });
      }, currentQuestion.delay);

      completeSurvey();
    }
  }, [currentQuestion, progress, navigate]);

  useEffect(() => {
    if (!currentQuestion.answers) {
      return;
    }

    setAnswers(null);
    setAnswers(shuffleAnswers(currentQuestion.answers));
  }, [currentQuestion.answers]);

  const skipSurveySelection = () => {
    setFade(false);
    goToNextQuestion();
  };

  const goToNextQuestion = () => {
    setTimeout(() => {
      setCurrentAnswer(null);
      setSelectedAnswers([]);
      setFade(true);
      setQuestionNumber((q) => Math.min(q + 1, STEP_COUNT));
    }, currentQuestion.delay);
  };

  const handleSurveySelection = (answer: string, answerKey: string, segmentAnswer: string) => {
    // filter out multiple clicks
    if (!!currentAnswer) {
      return;
    }

    setCurrentAnswer(answer);
    setFade(false);

    // only need to track answers, if there is one
    if (!!currentQuestion.trackEvent) {
      analyticsTrack(currentQuestion.trackEvent, {
        [currentQuestion.trackAs]: segmentAnswer || answer
      });

      saveEventToGoFormz(currentQuestion.trackGoFormzEvent, answerKey);
    }
    goToNextQuestion();
  };

  const handleMultiSurveySelection = (answer: SurveyAnswer) => {
    if (selectedAnswers.includes(answer)) {
      setSelectedAnswers(selectedAnswers.filter((a) => a !== answer));
    } else {
      setSelectedAnswers([...selectedAnswers, answer]);
    }
  };

  const submitMultiSurveySelection = () => {
    if (selectedAnswers.length === 0) {
      return;
    }

    setFade(false);

    if (!!currentQuestion.trackEvent) {
      analyticsTrack(currentQuestion.trackEvent, {
        [currentQuestion.trackAs]: selectedAnswers.map((answer) => answer.answer).join(', ')
      });

      saveEventToGoFormz(currentQuestion.trackGoFormzEvent, selectedAnswers.map((answer) => answer.key).join(', '));
    }

    goToNextQuestion();
  };

  return (
    <div>
      <div className="survey-page">
        <CSSTransition className="fade" timeout={500} classNames="fade" in={fade} nodeRef={questionRef}>
          <div className="survey-question-container" ref={questionRef}>
            {!currentQuestion.last ? (
              <>
                <h1 className="survey-question">{currentQuestion.title}</h1>
                <h3 className="survey-question-subtitle">{currentQuestion.subTitle}</h3>
                {answers && (
                  <Card.Group className={classNames('survey-answers', currentQuestion.className, constructionOrManufacturing ? 'fivecolumns' : 'fourcolumns')}>
                    {answers
                      .filter((option) => {
                        if ((option.answer === 'Procore' || option.answer === 'Egnyte') && !constructionOrManufacturing) {
                          return false;
                        }
                        return true;
                      })
                      .map((option, index) => (
                        <Card
                          key={option.key}
                          className={classNames('survey-answer', {
                            checked: currentAnswer === option.answer,
                            selected: selectedAnswers.includes(option) || currentAnswer === option.answer
                          })}
                          onMouseDown={(e) => e.preventDefault()} // Prevent focusing on click
                          onClick={() => {
                            (document.activeElement as HTMLElement)?.blur(); // Ensure focus is removed from the clicked element
                            if (!!currentQuestion.multiSelect) {
                              handleMultiSurveySelection(option);
                            } else {
                              handleSurveySelection(option.answer, option.key, option.segmentAnswer);
                            }
                          }}
                          tabIndex={index + 1}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (!!currentQuestion.multiSelect) {
                                handleMultiSurveySelection(option);
                              } else {
                                handleSurveySelection(option.answer, option.key, option.segmentAnswer);
                              }
                            }
                          }}
                        >
                          <Card.Content>
                            {questionNumber !== 3 && <div className="header">{option.answer}</div>}
                            {option.icon}
                            {questionNumber === 3 ? (
                              <div className="description">{option.answer}</div>
                            ) : (
                              option.description && <div className="description">{option.description}</div>
                            )}
                          </Card.Content>
                        </Card>
                      ))}
                  </Card.Group>
                )}
                {currentQuestion?.skippable && currentQuestion?.multiSelect && (
                  <div className="buttons">
                    <Button
                      className="tertiary skip"
                      onClick={skipSurveySelection}
                      tabIndex={answers.length + 1}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          skipSurveySelection();
                        }
                      }}
                    >
                      Skip
                    </Button>
                    <Button
                      secondary
                      className="next"
                      onClick={submitMultiSurveySelection}
                      disabled={selectedAnswers.length === 0}
                      tabIndex={answers?.length + 2}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          submitMultiSurveySelection();
                        }
                      }}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </CSSTransition>
        <Progress className={classNames('progress-bar', currentQuestion.className)} percent={progress} size="tiny" data-testid="progress-bar" />
      </div>
    </div>
  );
}
